import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ContainerPainel from 'components/container_painel';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IQuestoesAmbientais, useQuestoesAmbientaisStore } from 'stores/forms/questoes_ambientais.store';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import { ROUTES } from 'routes/constants';

interface ProblemaAmbiental {
  key: keyof IQuestoesAmbientais;
  label: string;
  prioridade: number | null;
}

const FormQuestoesAmbientaisVisualizar: React.FC = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { questoes, getQuestoes } = useQuestoesAmbientaisStore();
  const [openStatus, setOpenStatus] = useState<boolean>(true);

  const problemasAmbientais: {
    key: keyof IQuestoesAmbientais;
    label: string;
  }[] = [
      {
        key: 'prioridadePoucosImoveisRegularizados',
        label: 'Poucos imóveis rurais regularizados ambientalmente',
      },

      {
        key: 'prioridadeDegradacaoApp',
        label: 'Degradação das Áreas de Preservação Permanente (APP)',
      },
      {
        key: 'prioridadeDegradacaoSolo',
        label: 'Degradação do solo (processos erosivos, compactação, aventização, perda de fertilidade etc.)',
      },
      {
        key: 'prioridadeSemTratamentoResiduos',
        label: 'Ausência de tratamento de resíduos sólidos (lixo)',
      },
      {
        key: 'prioridadeDesmatamentoIlegal',
        label: 'Desmatamento ilegal e degradação florestal',
      },
      {
        key: 'prioridadeUsoAgrotoxicos',
        label: 'Uso indiscriminado de agrotóxicos',
      },
      {
        key: 'prioridadePerdaBiodiversidade',
        label: 'Perda da biodiversidade',
      },
      {
        key: 'prioridadeCriseHidrica',
        label: 'Crise Hídrica e perca de água',
      },
      {
        key: 'prioridadePoluicaoAgua',
        label: 'Poluição e contaminação da água',
      },
      {
        key: 'prioridadePoluicaoSolo',
        label: 'Poluição e contaminação do solo',
      },
      {
        key: 'prioridadeQueimadas',
        label: 'Queimadas e incêndios florestais',
      },
      {
        key: 'prioridadeMudancasClimaticas',
        label: 'Mudanças climáticas / Eventos extremos de seca e cheias',
      },
    ];

  // Organizar problemas por prioridade (1-12)
  const problemasPriorizados: ProblemaAmbiental[] = useMemo(() => {
    return problemasAmbientais
      .map(p => ({ ...p, prioridade: questoes[p.key] || null }))
      .filter(p => p.prioridade !== null && p.prioridade !== '')
      .sort((a, b) => (a.prioridade || 0) - (b.prioridade || 0));
  }, [questoes]);

  const anoRef = new Date().getFullYear() - 1;

  useEffect(() => {
    if (uuid) getQuestoes(uuid);
  }, [uuid, getQuestoes]);

  return (
    <ContainerPainel
      pagina='Formulário da Secretaria Municipal de Agricultura'
      showBackButton={true}
    >
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        {/* Cabeçalho simples, centralizado */}
        <Stack alignItems="center" textAlign="center" spacing={1} mb={3}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
            Visualizar Formulário de Questões Ambientais
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
            Visualização das Prioridades Ambientais do Município.
          </Typography>
        </Stack>

        {/* Conteúdo central com largura limitada, fundo branco */}
        <Stack marginX='4rem' paddingTop='2rem' spacing={2}>
          {/* Ano referência */}
          <Typography sx={{ fontSize: 16, color: 'text.secondary', mb: 2 }}>
            <strong>Ano referência do Questionário:</strong> {anoRef}
          </Typography>

          {/* Observações (azul-claro) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              bgcolor: '#EAF2FF',
              border: '1px solid #D5E4FF',
              borderRadius: 1,
              px: 2,
              py: 1.5,
              mb: 2.5,
            }}
          >
            <InfoOutlinedIcon sx={{ color: '#2962FF', mt: '2px' }} />
            <Box>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                • Problemas ambientais ordenados por prioridade (1 = mais significativo);
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                • Modo somente leitura - dados não podem ser editados.
              </Typography>
            </Box>
          </Box>

          {/* Seção: Problemas Ambientais Priorizados */}
          <Paper elevation={1} sx={{ p: 0 }}>
            <Box
              sx={{ px: 3, pt: 2, cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setOpenStatus(!openStatus)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                    color: '#000000DE',
                    fontSize: '20px',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {openStatus ? (
                    <KeyboardArrowUpOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                  ) : (
                    <KeyboardArrowDownOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                  )}
                  Problemas Ambientais Priorizados
                </Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
            </Box>

            {openStatus && (
              <Box sx={{ p: 3 }}>
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                    fontStyle: 'bold',
                    lineHeight: '160%',
                    letterSpacing: '0.15px',
                    color: '#000000DE'
                  }}
                >
                  Ranking de Importância
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '143%',
                    letterSpacing: '0.17px',
                    color: '#00000099'
                  }}
                >
                  Problemas ambientais mais significativos no meio rural do município (1 para o mais significativo, 2 para o segundo mais significativo e assim por diante).
                </Typography>
                <Divider sx={{ mt: 1 }} />

                <Stack spacing={2} sx={{ mt: 2 }}>
                  {problemasPriorizados.map((p) => (
                    <Box key={p.key}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 400,
                          lineHeight: '143%',
                          letterSpacing: '0.17px',
                          color: '#00000099'
                        }}
                      >
                        Prioridade
                      </Typography>

                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Chip
                          label={`${p.prioridade}º`}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 22,
                            opacity: 1,
                            borderRadius: '4px',
                            padding: '4px',
                            textAlign: 'center',
                            backgroundColor: '#EAEFF9',
                            border: 'none',
                            '& .MuiChip-label': {
                              px: 0,
                              fontSize: '16px',
                              fontWeight: 400,
                              lineHeight: '22px',
                              color: '#000',
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontWeight: 400,
                            lineHeight: '24px',
                            letterSpacing: '0.15px',
                            color: '#000000DE',
                          }}
                        >
                          {p.label}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Paper>
        </Stack>

        <Box display="flex" justifyContent="center" mt={4} mb={2}>
          <Button
            variant="text"
            onClick={() => navigate(ROUTES.PACOTE_FORMULARIOS)}
            sx={{ fontWeight: 600, letterSpacing: '0.5px' }}
          >
            VOLTAR
          </Button>
        </Box>
      </Box>
    </ContainerPainel>
  );
};

export default FormQuestoesAmbientaisVisualizar;
